// JSON-LD schema builders — always uses the canonical HTTPS non-www domain.
// Google flags fake AggregateRating (must reflect real user reviews), so we deliberately
// DO NOT invent star ratings. When you start collecting real product/blog reviews,
// pass `ratingValue` + `reviewCount` into buildProductLD to expose them.

import { SITE_URL } from "../hooks/usePageMeta";

const absUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

/** Breadcrumb list from an array of {name, path}. Last item is implicitly the current page. */
export function buildBreadcrumbLD(crumbs) {
  if (!crumbs || !crumbs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

/** Simple WebPage schema for generic pages (About/Contact/Resources/etc.). */
export function buildWebPageLD({ name, path, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    url: absUrl(path),
    description,
    isPartOf: {
      "@type": "WebSite",
      name: "BAISHALII",
      url: SITE_URL + "/",
    },
    publisher: {
      "@type": "Organization",
      name: "BAISHALII",
      url: SITE_URL + "/",
    },
  };
}

/** Product schema for /products/<slug>.  `product` is the object from DataContext. */
export function buildProductLD(product, opts = {}) {
  if (!product) return null;
  const path = `/products/${product.slug}`;
  const url = absUrl(path);
  const images = Array.isArray(product.images) && product.images.length
    ? product.images.map(absUrl)
    : product.image ? [absUrl(product.image)] : undefined;
  const doc = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name + (product.series ? ` — ${product.series} Series` : ""),
    description: product.tagline ? `${product.tagline}. ${product.desc || ""}`.trim() : product.desc,
    sku: product.slug,
    url,
    brand: { "@type": "Brand", name: "BAISHALII" },
    manufacturer: { "@type": "Organization", name: "BAISHALII", url: SITE_URL + "/" },
    category: product.categoryName,
  };
  if (images) doc.image = images.length === 1 ? images[0] : images;
  if (product.material) doc.material = product.material;
  // B2B "request-a-quote" Offer — satisfies Google's Product-snippet requirement
  // for an offer without publishing a specific price (enquiry-based sales).
  doc.offers = {
    "@type": "Offer",
    url,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    priceCurrency: "INR",
    price: "0",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "INR",
      valueAddedTaxIncluded: false,
      description: "Contact BAISHALII for dealer pricing and quote",
    },
    seller: { "@type": "Organization", name: "BAISHALII", url: SITE_URL + "/" },
    availableAtOrFrom: { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "IN" } },
  };
  // Real AggregateRating is only emitted when the caller provides real numbers
  if (opts.ratingValue && opts.reviewCount) {
    doc.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(opts.ratingValue),
      reviewCount: Number(opts.reviewCount),
      bestRating: 5, worstRating: 1,
    };
  }
  return doc;
}

/** CollectionPage schema for the /products index page. */
export function buildCollectionPageLD({ name, path, description, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url: absUrl(path),
    description,
    isPartOf: { "@type": "WebSite", name: "BAISHALII", url: SITE_URL + "/" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: (items || []).map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: absUrl(it.path),
        name: it.name,
      })),
    },
  };
}

/** BlogPosting schema for /blog/<slug>. `post` is the blog post from the API. */
export function buildBlogPostingLD(post) {
  if (!post) return null;
  const path = `/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || "",
    image: post.coverImage ? absUrl(post.coverImage) : undefined,
    datePublished: post.publishedAt || post.created_at || undefined,
    dateModified: post.updatedAt || post.publishedAt || post.created_at || undefined,
    author: {
      "@type": "Person",
      name: post.author || "BAISHALII Editorial",
    },
    publisher: {
      "@type": "Organization",
      name: "BAISHALII",
      url: SITE_URL + "/",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/apple-touch-icon.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(path) },
    url: absUrl(path),
    keywords: (post.tags || []).join(", ") || undefined,
  };
}

/**
 * FAQPage schema for a product page. Builds a small set of Q&A pairs
 * from the product's Features and Applications tabs (both of which are visible
 * on the same page — a Google requirement for FAQ schema).
 * Returns null if the product has no features or applications to describe.
 */
export function buildProductFAQLD(product) {
  if (!product) return null;
  const faqs = [];
  const displayName = product.name + (product.series ? ` ${product.series}` : "");

  const features = (product.features || []).filter(Boolean);
  if (features.length) {
    faqs.push({
      "@type": "Question",
      name: `What are the key features of BAISHALII ${displayName}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: features.join(". ") + (features[features.length - 1].endsWith(".") ? "" : "."),
      },
    });
  }

  const applications = (product.applications || []).filter(Boolean);
  if (applications.length) {
    faqs.push({
      "@type": "Question",
      name: `Where is BAISHALII ${displayName} typically used?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `BAISHALII ${displayName} is commonly used in: ${applications.join(", ")}.`,
      },
    });
  }

  if (product.material) {
    faqs.push({
      "@type": "Question",
      name: `What is BAISHALII ${displayName} made of?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `BAISHALII ${displayName} is manufactured using ${product.material}${product.tagline ? ` — ${product.tagline}` : ""}.`,
      },
    });
  }

  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs,
  };
}


/** Blog index (/blog) schema — a Blog entity + latest posts as blogPost list. */
export function buildBlogListLD({ name, path, description, posts }) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name,
    url: absUrl(path),
    description,
    publisher: { "@type": "Organization", name: "BAISHALII", url: SITE_URL + "/" },
    blogPost: (posts || []).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: absUrl(`/blog/${p.slug}`),
      datePublished: p.publishedAt || p.created_at || undefined,
    })),
  };
}
